import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Megaphone, FileText, Download, Settings, Mail } from "lucide-react";
import { InstallAppCard, PinnedWelcome } from "@/components/InstallAppCard";

export function Messages() {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => { api.get("/messages").then((r) => setMsgs(r.data)); }, []);
  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><Megaphone className="w-6 h-6 text-coral" /> Messages</h1>
      <p className="text-slate-500">Announcements from Adventure Rock.</p>
      <div className="mt-6 space-y-4"><PinnedWelcome /><InstallAppCard /></div>
      <div className="mt-6 space-y-3">
        {msgs.map((m) => (
          <div key={m.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`message-${m.id}`}>
            <p className="font-display font-bold text-navy">{m.title}</p>
            <p className="text-slate-500 text-sm mt-1">{m.body}</p>
            <p className="text-xs text-slate-300 mt-2">{new Date(m.created_at).toLocaleDateString()}</p>
          </div>
        ))}
        {msgs.length === 0 && <p className="text-slate-400">No messages.</p>}
      </div>
    </div>
  );
}

export function Documents() {
  const [data, setData] = useState({ receipts: [], documents: [] });
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/documents").then((r) => setData(r.data)); }, []);
  const download = async (r) => {
    setBusy(r.id);
    try {
      const res = await api.get(`/invoices/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${r.number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const total = data.receipts.reduce((s, r) => s + (r.total || 0), 0);
  const years = [...new Set(data.receipts.map((r) => r.date.slice(0, 4)))].sort().reverse();
  const [year, setYear] = useState("");
  const yr = year || years[0];
  const statement = async () => {
    setBusy("statement");
    try {
      const res = await api.get(`/invoices/statement/${yr}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Adventure-Rock-Statement-${yr}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><FileText className="w-6 h-6 text-turquoise" /> Documents</h1>
      <p className="text-slate-500">Every confirmed payment is saved here as an invoice you can download anytime.</p>
      {data.receipts.length > 0 && <p className="mt-4 text-sm text-navy" data-testid="invoices-total">{data.receipts.length} invoice{data.receipts.length === 1 ? "" : "s"} · <b>${total.toFixed(2)}</b> paid in total</p>}
      {years.length > 0 && (
        <div className="mt-4 bg-navy text-white rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid="yearly-statement">
          <div><p className="font-display font-bold">Yearly payment statement</p>
            <p className="text-xs text-white/60">One PDF with every payment for the year — for taxes or employer childcare claims.</p></div>
          <div className="flex items-center gap-2">
            {years.length > 1 && <select value={yr} onChange={(e) => setYear(e.target.value)} className="rounded-full bg-white/10 border border-white/20 px-3 h-9 text-sm" data-testid="statement-year">
              {years.map((y) => <option key={y} value={y} className="text-navy">{y}</option>)}</select>}
            <Button onClick={statement} disabled={busy === "statement"} className="rounded-full bg-gold text-navy font-bold" data-testid="statement-download"><Download className="w-4 h-4 mr-1" /> {yr} statement</Button>
          </div>
        </div>
      )}
      <div className="mt-4 space-y-3">
        {data.receipts.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between gap-3" data-testid={`receipt-${r.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-navy">{r.title}</p>
              <p className="text-xs text-slate-500 truncate">{r.items?.[0]?.explorers}{r.items?.[0]?.dates ? ` · ${r.items[0].dates}` : ""}</p>
              <p className="text-xs text-slate-400">#{r.number} · {new Date(r.date).toLocaleDateString()} · {r.waived ? "Waived" : r.method}</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-full shrink-0" disabled={busy === r.id} onClick={() => download(r)} data-testid={`receipt-download-${r.id}`}>
              <Download className="w-4 h-4 mr-1" /> PDF
            </Button>
          </div>
        ))}
        {data.receipts.length === 0 && <p className="text-slate-400">No invoices yet. Confirmed payments appear here.</p>}
      </div>
    </div>
  );
}

export function Account() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) setF({ name: user.name || "", phone: user.phone || "", email: user.email || "" }); }, [user]);
  const saveInfo = async () => {
    setBusy(true);
    try {
      await api.put("/auth/me", { name: f.name, phone: f.phone, email: f.email });
      toast.success("Account updated");
      setTimeout(() => window.location.reload(), 700);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const savePw = async () => {
    if (pw.new_password.length < 6) return toast.error("New password must be at least 6 characters.");
    setBusy(true);
    try {
      await api.put("/auth/me", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy flex items-center gap-2"><Settings className="w-6 h-6 text-navy" /> Account</h1>
      <p className="text-slate-500">Update your details and password.</p>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 max-w-md space-y-3" data-testid="account-info">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-navy/5 flex items-center justify-center font-display font-bold text-navy text-xl">{f.name?.[0]}</div>
          <div><p className="text-sm text-slate-500">Role: <span className="font-semibold text-navy capitalize">{user?.role}</span></p></div>
        </div>
        <div><Label className="text-xs text-slate-500">Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-lg h-10" data-testid="account-name" /></div>
        <div><Label className="text-xs text-slate-500">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-lg h-10" data-testid="account-phone" /></div>
        <div><Label className="text-xs text-slate-500 flex items-center gap-1"><Mail className="w-3 h-3" /> Email (your login)</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-lg h-10" data-testid="account-email" /></div>
        <Button onClick={saveInfo} disabled={busy} className="w-full bg-turquoise text-navy font-bold rounded-full" data-testid="account-save">Save details</Button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-4 max-w-md space-y-3" data-testid="account-password">
        <p className="font-display font-bold text-navy">Change password</p>
        <div><Label className="text-xs text-slate-500">Current password</Label><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="rounded-lg h-10" data-testid="account-current-pw" /></div>
        <div><Label className="text-xs text-slate-500">New password</Label><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="rounded-lg h-10" data-testid="account-new-pw" /></div>
        <Button onClick={savePw} disabled={busy} variant="outline" className="w-full rounded-full font-bold border-navy text-navy" data-testid="account-change-pw">Change password</Button>
      </div>
    </div>
  );
}
